import { GetStaticProps } from "next";
import Head from "next/head";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Layout from "../../components/layout";
import Link from "../../components/Link";
import CallToActionBlog from "../../components/v2/blog/call-to-action-blog";
import ClientTestimonials from "../../components/v2/mw-carousel/testimonial";
import ProductResources from "../../components/v2/product/product-resource";
import FeaturesMonitoring from "../../components/v2/tabs/features-monitoring";
import nextI18NextConfig from "../../next-i18next.config";
import "../../styles/scss/platform.module.scss";
import "../../styles/scss/_product-overview-page.module.scss";

export default function ProductOverview() {
    const { t } = useTranslation("common");

    const featuresData = t('product.product-overview.features.detailedFeatures', { returnObjects: true }) as any[]
    const features = Array.isArray(featuresData) ? featuresData : []

    const resourceData = t('product.product-overview.resources', { returnObjects: true }) as any[]
    const resource = Array.isArray(resourceData) ? resourceData : []


    return (
        <>
            <Head>
                <link
                    rel="preload"
                    as="image"
                    href="/images/product/product-overview-hero-mobile.webp"
                    media="(max-width: 768px)"
                    fetchPriority="high"
                />
                <link
                    rel="preload"
                    as="image"
                    href="/images/product/product-overview-hero.webp"
                    media="(min-width: 769px)"
                    fetchPriority="high"
                />
            </Head>
        <Layout
            pageTitle={"Middleware: Total Visibility Across Your Entire Stack"}
            uri={"/product/"}
            metaTitle={"Middleware: Total Visibility Across Your Entire Stack"}
            metaDesc={
                "Monitor apps, infrastructure, databases, containers, RUM, and LLMs in one platform. OpsAI detects, diagnoses, and resolves issues automatically. Start Free Now."
            }
            ogImage={"/images/product/product-overview-hero-og.webp"}
            metaRobotsNoindex={"index"}
            metaRobotsNofollow={"follow"}
            isHomePage={false}
        >
            <div className={"platform-main"}>

                <section className={`hero-section product-page product-overview-hero`}>
                <div className={'container'}>
                    <div className={"d-flex"}>
                        <div className={"left-block"}>
                            <div className={"hero-child"}>
                                <h1>{t('product.product-overview.hero.title')} <span>{t('product.product-overview.hero.titleHighlight')}</span></h1>
                                <p>{t('product.product-overview.hero.description')}</p>
                                <div className="d-flex">
                                        <Link
                                            href="https://app.middleware.io/auth/register/"
                                            target="_blank"
                                            className="sign-up ahref-btn primary"
                                        >
                                            Get Started Free
                                        </Link>

                                        <Link
                                            href={'/demo-request/'}
                                            className="sign-up ahref-btn secondary">
                                            Book a Demo
                                        </Link>
                                    </div>
                                    <p className={"tips"}>{t('product.hero.tips')}</p>

                            </div>
                        </div>
                        <div className={"right-block"}>
                             <Link href="https://www.youtube.com/watch?v=xKHsS2XKseA" target="_blank">
                                <picture>
                                    <source
                                        media="(max-width: 768px)"
                                        srcSet="/images/product/product-overview-hero-mobile.webp"
                                    />
                                    <img
                                        src="/images/product/product-overview-hero.webp"
                                        alt={t('product.product-overview.hero.imageAlt')}
                                        width={1360}
                                        height={1040}
                                        fetchPriority="high"
                                        loading="eager"
                                        decoding="async"
                                    />
                                </picture>
                             </Link>

                        </div>
                    </div>
                </div>
            </section>

                <FeaturesMonitoring productFeatures={features}
                    title={<h2>{t('product.product-overview.features.title')}</h2>} tourId={""} isTryOut={true} className="product-overview-page" hasActionsDemo />

                <CallToActionBlog
                    title={'Everything Connected. Every Issue Resolved. One Platform.'}
                    isButton={true}
                    isSignupBtn={true}
                />

                <ClientTestimonials />

                <ProductResources resources={resource} title={t('product.shared.resources.title')} />

            </div>
        </Layout>
        </>
    )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
    return {
        props: {
            pageTitle: "Middleware: Total Visibility Across Your Entire Stack",
            description: "Monitor apps, infrastructure, databases, containers, RUM, and LLMs in one platform. OpsAI detects, diagnoses, and resolves issues automatically. Start Free Now.",
            ogImage: "/images/product/product-overview-hero-og.webp",
            uri: "/product/",
            metaTitle: "Middleware: Total Visibility Across Your Entire Stack",
            metaDesc: "Monitor apps, infrastructure, databases, containers, RUM, and LLMs in one platform. OpsAI detects, diagnoses, and resolves issues automatically. Start Free Now.",
            metaRobotsNoindex: "index",
            metaRobotsNofollow: "follow",
            ...(await serverSideTranslations(locale ?? 'en', ['common', 'header', 'footer', 'home'], nextI18NextConfig)),
        },
    }
}